import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { OdataResponseItem } from '../models/odata.model';
import {
  ClientTokens,
  ResponseClientTokens,
  ResponseWebhook,
  SystemWebhook,
  UserWebhook,
  Webhook
} from '../models/webhook.model';
import { WebhookAdapter } from '../utils/adapters/webhook-adapter';
import { AppConfigService } from './app-config.service';
import { UserInfoService } from './user-info.service';

@Injectable({
  providedIn: 'root'
})
export class IntegrationsService {
  constructor(
    private appConfigService: AppConfigService,
    private http: HttpClient,
    private userInfoService: UserInfoService
  ) {}

  getUserWebhooks(id?: string): Observable<UserWebhook[]> {
    return this.http
      .get<
        OdataResponseItem<ResponseWebhook>[]
      >(`${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/user${id ? '/bypersonid/' + id : ''}`)
      .pipe(
        map((response) => WebhookAdapter.adaptUserWebhooks(response)),
        tap((webhooks) => this.hydrateWebhookSites(webhooks))
      );
  }

  hydrateWebhookSites(webhooks: Webhook[]) {
    const appSites = this.userInfoService.getTenantSites();
    for (const webhook of webhooks) {
      if (webhook.sites) {
        webhook.sites = webhook.sites.map((site) => {
          const appSite = appSites.find(({ id }) => id === site.id);

          return appSite ?? site;
        });
      }
    }
  }

  createUserWebhook(webhook: UserWebhook) {
    return this.http.post(
      `${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/user`,
      WebhookAdapter.adaptUserWebhookToRequest(webhook)
    );
  }

  editUserWebhook(webhook: UserWebhook) {
    return this.http.patch(
      `${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/user/${webhook.id}`,
      WebhookAdapter.adaptUserWebhookToRequest(webhook)
    );
  }

  deleteUserWebhook(id: string) {
    return this.http.delete(
      `${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/user/${id}`
    );
  }

  enableUserWebhook(id: string) {
    return this.http.patch(
      `${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/user/${id}`,
      { isactive: true }
    );
  }

  disableUserWebhook(id: string) {
    return this.http.patch(
      `${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/user/${id}`,
      { isactive: false }
    );
  }

  getSystemWebhooks(): Observable<SystemWebhook[]> {
    return this.http
      .get<
        OdataResponseItem<ResponseWebhook>[]
      >(`${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/system`)
      .pipe(
        map((response) => WebhookAdapter.adaptSystemWebhooks(response)),
        tap((webhooks) => this.hydrateWebhookSites(webhooks))
      );
  }

  createSystemWebhook(webhook: SystemWebhook) {
    return this.http.post(
      `${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/system`,
      WebhookAdapter.adaptSystemWebhookToRequest(webhook)
    );
  }

  editSystemWebhook(webhook: SystemWebhook) {
    return this.http.patch(
      `${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/system/${webhook.id}`,
      WebhookAdapter.adaptSystemWebhookToRequest(webhook)
    );
  }

  enableSystemWebhook(id: string) {
    return this.http.patch(
      `${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/system/${id}`,
      { isactive: true }
    );
  }

  disableSystemWebhook(id: string) {
    return this.http.patch(
      `${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/system/${id}`,
      { isactive: false }
    );
  }

  deleteSystemWebhook(id: string) {
    return this.http.delete(
      `${this.appConfigService.mhmApiHost}/webhooks/api/webhooks/system/${id}`
    );
  }

  fetchClientTokens(): Observable<ClientTokens> {
    return this.http
      .get<ResponseClientTokens>(
        `${this.appConfigService.mhmApiHost}/clients/api/client`
      )
      .pipe(map((res) => WebhookAdapter.adaptClientTokens(res)));
  }

  refreshClientSecretId(clientId: string) {
    return this.http.post(
      `${this.appConfigService.mhmApiHost}/clients/api/clients/${clientId}/rotate-secret`,
      {}
    );
  }

  validateSystemWebhookName(name: string): Observable<boolean> {
    return this.getSystemWebhooks().pipe(
      map(
        (webhooks) =>
          !webhooks.some(
            (webhook) => webhook.name.toLowerCase() === name.toLowerCase()
          )
      )
    );
  }
}
